import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { 
  fetchUserColorPreferences, 
  saveUserColorPreferences, 
  deleteUserColorPreferences,
  applyColors,
  getDefaultColors
} from '../api';
import { generateComplementaryColors } from '../../colors/ColorUtils';
import ColorPresets from './ColorPresets';
import SaveNotification from './SaveNotification';

/**
 * Color settings component
 * @returns {React.ReactElement} - The color settings component
 */
const ColorSettings = () => {
  const [primaryColor, setPrimaryColor] = useState('');
  const [primaryLightColor, setPrimaryLightColor] = useState('');
  const [primaryDarkColor, setPrimaryDarkColor] = useState('');
  const [secondaryColor, setSecondaryColor] = useState('');
  const [secondaryLightColor, setSecondaryLightColor] = useState('');
  const [secondaryDarkColor, setSecondaryDarkColor] = useState('');
  const [showNotification, setShowNotification] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const currentUser = sessionStorage.getItem('user_id');
  const COLORS = getDefaultColors();

  // Load saved colors on component mount
  useEffect(() => {
    const loadUserColors = async () => {
      try {
        setIsLoading(true);

        if (!currentUser) {
          setDefaultColors();
          return;
        }

        // Fetch user color preferences
        const preferences = await fetchUserColorPreferences(currentUser);

        if (preferences) {
          // Set the state with saved colors
          setPrimaryColor(preferences.PRIMARY);
          setPrimaryLightColor(preferences.PRIMARY_LIGHT);
          setPrimaryDarkColor(preferences.PRIMARY_DARK);
          setSecondaryColor(preferences.SECONDARY);
          setSecondaryLightColor(preferences.SECONDARY_LIGHT);
          setSecondaryDarkColor(preferences.SECONDARY_DARK);

          // Apply the saved colors to CSS variables
          applyColors(preferences);
          console.log("Loaded saved colors from API:", preferences);
        } else {
          setDefaultColors();
        }
      } catch (error) {
        console.error("Error loading color preferences:", error);
        setDefaultColors();
      } finally {
        setIsLoading(false);
      }
    };

    loadUserColors();
  }, [currentUser]);

  // Set default colors
  const setDefaultColors = () => {
    // Set state with default colors
    setPrimaryColor(COLORS.PRIMARY);
    setPrimaryLightColor(COLORS.PRIMARY_LIGHT);
    setPrimaryDarkColor(COLORS.PRIMARY_DARK);
    setSecondaryColor(COLORS.SECONDARY);
    setSecondaryLightColor(COLORS.SECONDARY_LIGHT);
    setSecondaryDarkColor(COLORS.SECONDARY_DARK);

    // Apply default colors
    applyColors(COLORS);
    console.log("Applied default colors:", COLORS);
  };

  // Function to update colors in API and apply them
  const updateColors = async () => {
    try {
      if (!currentUser) {
        toast.error('You must be logged in to save preferences');
        return;
      }

      const colorData = {
        PRIMARY: primaryColor,
        PRIMARY_LIGHT: primaryLightColor,
        PRIMARY_DARK: primaryDarkColor,
        SECONDARY: secondaryColor,
        SECONDARY_LIGHT: secondaryLightColor,
        SECONDARY_DARK: secondaryDarkColor
      };

      // Save to API
      const success = await saveUserColorPreferences(currentUser, colorData);

      if (success) {
        // Apply the updated colors
        applyColors(colorData);
        console.log("Saved colors to API:", colorData);

        // Show notification
        toast.success('Colors saved successfully!');
        setShowNotification(true);
        setTimeout(() => setShowNotification(false), 3000);
      } else {
        toast.error('Failed to save color preferences');
      }
    } catch (error) {
      console.error("Error saving color preferences:", error);
      toast.error('Failed to save color preferences');
    }
  };

  // Handle preset selection
  const handlePresetSelect = (presetColors) => {
    setPrimaryColor(presetColors.PRIMARY);
    setPrimaryLightColor(presetColors.PRIMARY_LIGHT);
    setPrimaryDarkColor(presetColors.PRIMARY_DARK);
    setSecondaryColor(presetColors.SECONDARY);
    setSecondaryLightColor(presetColors.SECONDARY_LIGHT);
    setSecondaryDarkColor(presetColors.SECONDARY_DARK);

    // Apply the colors locally without saving to API
    applyColors(presetColors);
    console.log("Applied preset colors locally:", presetColors);

    // Show notification
    toast.success('Preset applied! Click Save Changes to persist.');
  };

  // Generate complementary colors based on primary color
  const generateComplement = () => {
    const colorSet = generateComplementaryColors(primaryColor);
    setPrimaryLightColor(colorSet.PRIMARY_LIGHT);
    setPrimaryDarkColor(colorSet.PRIMARY_DARK);
    setSecondaryColor(colorSet.SECONDARY);
    setSecondaryLightColor(colorSet.SECONDARY_LIGHT);
    setSecondaryDarkColor(colorSet.SECONDARY_DARK);
  };

  // Reset to default colors
  const resetToDefaults = async () => {
    try {
      if (currentUser) {
        // Delete from API
        await deleteUserColorPreferences(currentUser);
      }

      // Set and apply default colors
      setDefaultColors();
      console.log("Reset to default colors");

      // Show notification
      toast.success('Colors reset to defaults!');
    } catch (error) {
      console.error("Error resetting colors:", error);
      toast.error('Failed to reset colors');

      // Still apply default colors locally even if API delete fails
      setDefaultColors();
    }
  };

  // If colors haven't loaded yet, show a loading state
  if (isLoading) {
    return <div className="p-6 text-center">Loading color settings...</div>;
  }

  return (
    <div className="p-6  mx-auto">
      {/* <div className="bg-white p-6 rounded-xl shadow-md mb-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="text-left">
            <h1 className="text-3xl font-medium text-primary mb-1">Color Settings</h1>
          </div>
        </div>
      </div> */}

      {/* Color Presets */}
      <ColorPresets onSelectPreset={handlePresetSelect} />

      {/* Primary and Secondary Color Settings */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4 text-primary">Primary Colors</h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* Primary Color */}
          <div>
            <label className="block text-gray-600 mb-2">Primary Color</label>
            <div className="flex items-center">
              <input
                type="color"
                value={primaryColor}
                onChange={(e) => setPrimaryColor(e.target.value)}
                className="h-10 w-10 rounded mr-2"
              />
              <input
                type="text"
                value={primaryColor}
                onChange={(e) => setPrimaryColor(e.target.value)}
                className="border border-gray-300 rounded px-3 py-2 w-full"
              />
            </div>
          </div>

          {/* Primary Light */}
          <div>
            <label className="block text-gray-600 mb-2">Primary Light</label>
            <div className="flex items-center">
              <input
                type="color"
                value={primaryLightColor}
                onChange={(e) => setPrimaryLightColor(e.target.value)}
                className="h-10 w-10 rounded mr-2"
              />
              <input
                type="text"
                value={primaryLightColor}
                onChange={(e) => setPrimaryLightColor(e.target.value)}
                className="border border-gray-300 rounded px-3 py-2 w-full"
              />
            </div>
          </div>

          {/* Primary Dark */}
          <div>
            <label className="block text-gray-600 mb-2">Primary Dark</label>
            <div className="flex items-center">
              <input
                type="color"
                value={primaryDarkColor}
                onChange={(e) => setPrimaryDarkColor(e.target.value)}
                className="h-10 w-10 rounded mr-2"
              />
              <input
                type="text"
                value={primaryDarkColor}
                onChange={(e) => setPrimaryDarkColor(e.target.value)}
                className="border border-gray-300 rounded px-3 py-2 w-full"
              />
            </div>
          </div>
        </div>

        <button
          onClick={generateComplement}
          className="mb-6 px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition"
        >
          Generate Complementary Colors
        </button>

        <h2 className="text-xl font-semibold mb-4 text-secondary">Secondary Colors</h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* Secondary Color */}
          <div>
            <label className="block text-gray-600 mb-2">Secondary Color</label>
            <div className="flex items-center">
              <input
                type="color"
                value={secondaryColor}
                onChange={(e) => setSecondaryColor(e.target.value)}
                className="h-10 w-10 rounded mr-2"
              />
              <input
                type="text"
                value={secondaryColor}
                onChange={(e) => setSecondaryColor(e.target.value)}
                className="border border-gray-300 rounded px-3 py-2 w-full"
              />
            </div>
          </div>

          {/* Secondary Light */}
          <div>
            <label className="block text-gray-600 mb-2">Secondary Light</label>
            <div className="flex items-center">
              <input
                type="color"
                value={secondaryLightColor}
                onChange={(e) => setSecondaryLightColor(e.target.value)}
                className="h-10 w-10 rounded mr-2"
              />
              <input
                type="text"
                value={secondaryLightColor}
                onChange={(e) => setSecondaryLightColor(e.target.value)}
                className="border border-gray-300 rounded px-3 py-2 w-full"
              />
            </div>
          </div>

          {/* Secondary Dark */}
          <div>
            <label className="block text-gray-600 mb-2">Secondary Dark</label>
            <div className="flex items-center">
              <input
                type="color"
                value={secondaryDarkColor}
                onChange={(e) => setSecondaryDarkColor(e.target.value)}
                className="h-10 w-10 rounded mr-2"
              />
              <input
                type="text"
                value={secondaryDarkColor}
                onChange={(e) => setSecondaryDarkColor(e.target.value)}
                className="border border-gray-300 rounded px-3 py-2 w-full"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-between mt-6">
          <button
            onClick={resetToDefaults}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition"
          >
            Reset to Defaults
          </button>

          <button
            onClick={updateColors}
            className="px-4 py-2 bg-secondary text-white rounded hover:bg-secondaryDark transition"
          >
            Save Changes
          </button>
        </div>
      </div>

      {/* Color Preview Section */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold mb-4">Color Preview</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Primary Colors Preview */}
          <div>
            <h3 className="font-medium mb-3">Primary Colors</h3>
            <div className="flex flex-col space-y-2">
              <div className="flex items-center">
                <div
                  className="w-16 h-8 rounded mr-2"
                  style={{ backgroundColor: primaryColor }}
                />
                <span>Primary</span>
              </div>
              <div className="flex items-center">
                <div
                  className="w-16 h-8 rounded mr-2"
                  style={{ backgroundColor: primaryLightColor }}
                />
                <span>Primary Light</span>
              </div>
                            <div className="flex items-center">
                <div
                  className="w-16 h-8 rounded mr-2"
                  style={{ backgroundColor: primaryDarkColor }}
                />
                <span>Primary Dark</span>
              </div>
            </div>
          </div>

          {/* Secondary Colors Preview */}
          <div>
            <h3 className="font-medium mb-3">Secondary Colors</h3>
            <div className="flex flex-col space-y-2">
              <div className="flex items-center">
                <div
                  className="w-16 h-8 rounded mr-2"
                  style={{ backgroundColor: secondaryColor }}
                />
                <span>Secondary</span>
              </div>
              <div className="flex items-center">
                <div
                  className="w-16 h-8 rounded mr-2"
                  style={{ backgroundColor: secondaryLightColor }}
                />
                <span>Secondary Light</span>
              </div>
              <div className="flex items-center">
                <div
                  className="w-16 h-8 rounded mr-2"
                  style={{ backgroundColor: secondaryDarkColor }}
                />
                <span>Secondary Dark</span>
              </div>
            </div>
          </div>
        </div>

        {/* UI Elements Preview */}
        <div className="mt-8">
          <h3 className="font-medium mb-3">UI Elements Preview</h3>
          <div className="space-y-4">
            <div className="flex space-x-2">
              <button className="px-4 py-2 bg-primary text-white rounded">
                Primary Button
              </button>
              <button className="px-4 py-2 bg-secondary text-white rounded">
                Secondary Button
              </button>
            </div>
            <div className="p-4 bg-primaryLight text-white rounded">
              This is a primary background container
            </div>
            <div className="p-4 bg-secondaryLight text-secondaryDark rounded">
              This is a secondary background container
            </div>
          </div>
        </div>
      </div>

      {/* Custom notification if toast is not available */}
      <SaveNotification
        visible={showNotification}
        message="Colors saved successfully!"
      />
    </div>
  );
};

export default ColorSettings;